import React from 'react'
import {
  createBrowserRouter,
  Route,
  useLocation,
  ScrollRestoration,
} from 'react-router-dom'
import {
  createRoutesFromElements,
  Navigate,
  Outlet,
  RouterProvider,
  Routes,
} from 'react-router'
import * as ROUTES from 'constants/routes'
import Users from 'components/Users/Users'
import { Login } from 'components/Login/Login'
import PasswordForgetPage from 'components/PasswordForget'
import UpcomingShifts from 'components/Shifts/upcoming_shifts'
import SingleShift from 'components/Shifts/single_shift'
import OngoingShifts from 'components/Shifts/ongoing_shifts'
import PastShifts from 'components/Shifts/past_shifts'
import Invoices from 'components/features/payments'
import AccountPage from 'components/Account'
import Listings from 'components/features/listing'
import WorkerBusinessReferral from 'components/features/WorkerBusinessReferral'
import { ReviewPage } from 'pages/review'
import { BlockWorkerPage } from 'pages/review/BlockWorker'
import Messaging2 from 'components/messaging2/messaging'
import { Positions } from './Positions/Positions'
import QuestDefinitions from 'components/features/quest_definitions'
import TipsContainer from 'components/Tips/TipsContainer'

import Spreadsheets from 'components/features/Spreadsheets'
import SidebarNav from 'components/Navigation/SidebarNav'
import 'bootstrap/dist/css/bootstrap.min.css'
import { AuthGuard } from 'components/auth'
import { WhoamiProvider } from 'components/Whoami'
import { NotFound } from 'pages/NotFound'
import UpcomingListings from './UpcomingListings'
import UnprocessedI9FormsPage from 'pages/e-verify/processing'
import { I9Management } from 'pages/e-verify/i9ReverificationQueue'
import WorkerSupply from 'components/features/WorkerSupply/WorkerSupply'
import VerifyCredentials from 'pages/VerifyCredentials/VerifyCredentials'
import { EventLogs } from 'pages/EventLogs'
import { CompanySwitch } from './Company/CompanySwitch/Switch'
import { PositionEditor } from 'components/PositionEditor'
import { CompanyDetail } from './Company/CompanyDetail/CompanyDetail'
import { CompanyGroupDetail } from './CompanyGroupDetail'
import { ShiftDetail } from './Shifts/ShiftDetail/ShiftDetail'
import { CommandPanel } from 'components/CommandPanel'
import { CommandPalette } from 'components/CommandPalette'
import CommandsPage from './Commands/CommandsPage'
import { CommandProvider } from 'components/features/Commands/CommandProvider'
import { GlobalWorkflows } from '../global-workflows'
import { CompanyGroups } from './CompanyGroups'
import { Companies } from './Companies'
import { Shifts } from './Shifts/Shifts'
import { BulkCreateShifts } from './Shifts/BulkCreateShifts'
import InvoiceSync from 'components/features/InvoiceSync'
import VmsTimesheetUpload from 'components/features/VmsTimesheetUpload'
import { Playground } from './Playground'
import { SkillsCollection } from './Playground/VoiceAgent/Coach/SkillsCollection'
import { Assessments } from './Playground/VoiceAgent/Coach/Assessments'
import { HighnoteNotificationTasks } from './Playground/HighnoteNotificationTasks'
import { HttpServices } from './Playground/HttpServices'
import WorkStatusSummary from './W2/WorkStatusSummary'
import PendingPayments from './W2/PendingPayments'
import WorkItemsInReview from './W2/WorkItemsInReview'
import Topup from './Highnote/Topup'
import { UserPayments } from './UserPayments'
import { UserDetail } from './UserDetail'
import { UnpaidShifts } from './UnpaidShifts'
import { BillRates } from './BillRates/BillRates'
import { Charges } from './Charges'
import { InvoiceConfig } from './InvoiceConfig'
import { InvoiceIssues } from './InvoiceIssues'
import { DayforceWebhooks } from './Dayforce/DayforceWebhooks'
import { DayforceWebhookDetail } from './Dayforce/DayforceWebhookDetail'
import { DayforceJobDetail } from './Dayforce/DayforceJobDetail'
import { DayforceJobsIssues } from './Dayforce/DayforceJobsIssues'
import { Jurisdictions } from './Wage/Jurisdictions'
import { JurisdictionDetail } from './Wage/JurisdictionDetail'
import { Rules } from './Wage/Rules'
import { RuleDetail } from './Wage/RuleDetail'
import { DayforceBookings } from './Dayforce/DayforceBookings'
import { Dayforce } from './Dayforce/Dayforce'
import { Vndly } from './Vndly'
import { VndlyIssues } from './VndlyIssues/VndlyIssues'
import { ColumbiaTimesheet } from './Vndly/ColumbiaTimesheet'
import { IndeedFlex } from './IndeedFlex/IndeedFlex'
import { IndeedFlexCompanies } from './IndeedFlex/IndeedFlexCompanies'
import { IndeedFlexShiftIssues } from './IndeedFlex/IndeedFlexShiftIssues'
import { IndeedFlexJobShifts } from './IndeedFlex/IndeedFlexJobShifts'
import { IndeedFlexOtp } from './IndeedFlex/IndeedFlexOtp'
import { Fieldglass } from './Fieldglass'
import { FieldglassShiftIssues } from './Fieldglass/FieldglassShiftIssues'
import { FieldglassJobPosting } from './Fieldglass/FieldglassJobPosting'
import { Jet } from './Jet'
import { JetStatus } from './Jet/JetStatus/JetStatus'
import { JetAlerts } from './Jet/JetAlerts/JetAlerts'
import { JetAssignments } from './Jet/JetAssignments/JetAssignments'
import { TimeClocks } from './TimeClocks'
import { CompumaticClockPunches } from './CompumaticClockPunches'
import { UserPermissions } from './UserPermissions'
import { PositionDetail } from './Positions/PositionDetail'
import { RequirementDetail } from './Requirements/RequirementDetail'
import { AssessmentDetail } from './Assessments/AssessmentDetail'
import { AssessmentsQueue } from './Assessments/AssessmentsQueue'

const PageRoutes = () => {
  const location = useLocation()
  const backgroundStack =
    (location.state && location.state.backgroundStack) || []

  return (
    <CommandProvider>
      <div
        style={{
          display: 'flex',
          height: '100vh',
          width: '100%',
          position: 'fixed',
        }}
      >
        <SidebarNav />
        <main
          style={{ overflowY: 'auto', flex: 1, paddingBottom: '80px' }}
          className="main-container"
        >
          <ScrollRestoration />
          <Routes
            location={
              backgroundStack.length > 0 ? backgroundStack[0] : location
            }
          >
            <Route
              path={ROUTES.LANDING}
              element={
                <AuthGuard.Authenticated
                  fallback={<Navigate to={ROUTES.SIGNIN} />}
                >
                  <WhoamiProvider>
                    <Users />
                  </WhoamiProvider>
                </AuthGuard.Authenticated>
              }
            />
            <Route path={ROUTES.SIGNIN} element={<Login />} />
            <Route
              path={ROUTES.PASSWORD_FORGET}
              element={<PasswordForgetPage />}
            />
            <Route
              path="/"
              element={
                <AuthGuard.Authenticated
                  fallback={<Navigate to={ROUTES.SIGNIN} />}
                >
                  <WhoamiProvider>
                    <GlobalWorkflows>
                      <Outlet />
                    </GlobalWorkflows>
                  </WhoamiProvider>
                </AuthGuard.Authenticated>
              }
            >
              <Route path={ROUTES.USERS} element={<Users />} />
              <Route
                path={ROUTES.UPCOMING_SHIFTS}
                element={<UpcomingShifts />}
              />
              <Route path={ROUTES.SHIFTS} element={<Shifts />} />
              <Route path={ROUTES.SHIFT_DETAIL} element={<ShiftDetail />} />
              <Route path={ROUTES.SINGLE_SHIFT} element={<SingleShift />} />
              <Route path={ROUTES.ONGOING_SHIFTS} element={<OngoingShifts />} />
              <Route path={ROUTES.PAST_SHIFTS} element={<PastShifts />} />
              <Route
                path={ROUTES.BULK_CREATE_SHIFTS}
                element={<BulkCreateShifts />}
              />
              <Route path={ROUTES.USER_PAYMENTS} element={<UserPayments />} />
              <Route path={ROUTES.USER_DETAIL} element={<UserDetail />} />
              <Route path={ROUTES.UNPAID_SHIFTS} element={<UnpaidShifts />} />
              <Route path={ROUTES.INVOICES} element={<Invoices />} />
              <Route path={ROUTES.CHARGES} element={<Charges />} />
              <Route path={ROUTES.BILL_RATES} element={<BillRates />} />
              <Route path={ROUTES.TIME_CLOCKS} element={<TimeClocks />} />
              <Route
                path={ROUTES.COMPUMATIC_CLOCK_PUNCHES}
                element={<CompumaticClockPunches />}
              />
              <Route path={ROUTES.INVOICE_CONFIG} element={<InvoiceConfig />} />
              <Route
                path="/invoice_config"
                element={<Navigate to={ROUTES.INVOICE_CONFIG} replace />}
              />
              <Route path={ROUTES.INVOICE_ISSUES} element={<InvoiceIssues />} />
              <Route
                path={ROUTES.USER_PERMISSIONS}
                element={<UserPermissions />}
              />
              <Route path={ROUTES.ACCOUNT} element={<AccountPage />} />
              <Route path={ROUTES.LISTINGS} element={<Listings />} />
              <Route path={ROUTES.COMPANIES} element={<Companies />} />
              <Route path={ROUTES.COMPANY_DETAIL} element={<CompanyDetail />} />
              <Route path={ROUTES.COMPANY_SWITCH} element={<CompanySwitch />} />
              <Route path={ROUTES.COMPANY_GROUPS} element={<CompanyGroups />} />
              <Route
                path={ROUTES.COMPANY_GROUP_DETAIL}
                element={<CompanyGroupDetail />}
              />
              <Route
                path={ROUTES.WORKERBIZREFERRALS}
                element={<WorkerBusinessReferral />}
              />
              <Route path={ROUTES.REVIEW} element={<ReviewPage />} />
              <Route
                path={ROUTES.REVIEW_BLOCK_WORKER}
                element={<BlockWorkerPage />}
              />
              <Route path={ROUTES.EVENT_LOGS} element={<EventLogs />} />

              <Route path={ROUTES.MESSAGES2} element={<Messaging2 />} />
              <Route path={ROUTES.POSITIONS} element={<Positions />} />
              <Route
                path={ROUTES.POSITION_DETAIL}
                element={<PositionDetail />}
              />
              <Route
                path={ROUTES.REQUIREMENT_DETAIL}
                element={<RequirementDetail />}
              />
              <Route
                path={ROUTES.WORKER_ASSESSMENT_DETAIL}
                element={<AssessmentDetail />}
              />
              <Route
                path={ROUTES.REVIEW_ASSESSMENTS_QUEUE}
                element={<AssessmentsQueue />}
              />
              <Route
                path={ROUTES.QUEST_DEFINITIONS}
                element={<QuestDefinitions />}
              />
              <Route path={ROUTES.TIPS} element={<TipsContainer />} />
              <Route path={ROUTES.COMMANDS} element={<CommandsPage />} />
              <Route
                path={ROUTES.POSITION_EDITOR}
                element={<PositionEditor />}
              />
              <Route path={ROUTES.SPREADSHEETS} element={<Spreadsheets />} />
              <Route
                path={ROUTES.EVERIFY_UNPROCESS_FORMS}
                element={<UnprocessedI9FormsPage />}
              />
              <Route
                path={ROUTES.EVERIFY_I9_REVERIFICATION_QUEUE}
                element={<I9Management />}
              />
              <Route path={ROUTES.PLAYGROUND} element={<Playground />} />
              <Route
                path={ROUTES.PLAYGROUND_COACH_SKILLS_COLLECTION}
                element={<SkillsCollection />}
              />
              <Route
                path={ROUTES.PLAYGROUND_COACH_ASSESSMENTS}
                element={<Assessments />}
              />
              <Route
                path={ROUTES.PLAYGROUND_HIGHNOTE_NOTIFICATION_TASKS}
                element={<HighnoteNotificationTasks />}
              />
              <Route
                path={ROUTES.PLAYGROUND_HTTP_SERVICES}
                element={<HttpServices />}
              />
              <Route path={ROUTES.HIGHNOTE_TOPUP} element={<Topup />} />
              <Route path={ROUTES.INVOICE_SYNC} element={<InvoiceSync />} />
              <Route
                path={ROUTES.VMS_TIMESHEET_UPLOAD}
                element={<VmsTimesheetUpload />}
              />
              <Route
                path={ROUTES.W2_WORK_STATUS}
                element={<WorkStatusSummary />}
              />
              <Route
                path={ROUTES.W2_PENDING_PAYMENTS}
                element={<PendingPayments />}
              />
              <Route
                path={ROUTES.W2_ITEMS_IN_REVIEW}
                element={<WorkItemsInReview />}
              />
              <Route path={ROUTES.DAYFORCE} element={<Dayforce />} />
              <Route
                path={ROUTES.DAYFORCE_BOOKINGS}
                element={<DayforceBookings />}
              />
              <Route
                path={ROUTES.DAYFORCE_JOB_DETAIL}
                element={<DayforceJobDetail />}
              />
              <Route
                path={ROUTES.DAYFORCE_WEBHOOKS}
                element={<DayforceWebhooks />}
              />
              <Route
                path={ROUTES.DAYFORCE_WEBHOOK_DETAIL}
                element={<DayforceWebhookDetail />}
              />
              <Route
                path={ROUTES.DAYFORCE_JOBS_ISSUES}
                element={<DayforceJobsIssues />}
              />
              <Route path={ROUTES.VNDLY} element={<Vndly />} />
              <Route path={ROUTES.VNDLY_ISSUES} element={<VndlyIssues />} />
              <Route
                path={ROUTES.VNDLY_COLUMBIA_TIMESHEET}
                element={<ColumbiaTimesheet />}
              />
              <Route path={ROUTES.INDEED_FLEX} element={<IndeedFlex />} />
              <Route
                path={ROUTES.INDEED_FLEX_SHIFT_ISSUES}
                element={<IndeedFlexShiftIssues />}
              />
              <Route
                path={ROUTES.INDEED_FLEX_JOB_SHIFTS}
                element={<IndeedFlexJobShifts />}
              />
              <Route
                path={ROUTES.INDEED_FLEX_COMPANIES}
                element={<IndeedFlexCompanies />}
              />
              <Route
                path={ROUTES.INDEED_FLEX_OTP}
                element={<IndeedFlexOtp />}
              />
              <Route path={ROUTES.FIELDGLASS} element={<Fieldglass />} />
              <Route
                path={ROUTES.FIELDGLASS_SHIFT_ISSUES}
                element={<FieldglassShiftIssues />}
              />
              <Route path={ROUTES.JET} element={<Jet />} />
              <Route
                path={ROUTES.JET_CALL_REPORT_SYNC_STATUS}
                element={<JetStatus />}
              />
              <Route path={ROUTES.JET_ALERTS} element={<JetAlerts />} />
              <Route
                path={ROUTES.JET_ASSIGNMENTS}
                element={<JetAssignments />}
              />
              <Route
                path={ROUTES.FIELDGLASS_JOB_POSTING}
                element={<FieldglassJobPosting />}
              />
              <Route
                path={ROUTES.WAGE_JURISDICTIONS}
                element={<Jurisdictions />}
              />
              <Route
                path={ROUTES.WAGE_JURISDICTION_DETAIL}
                element={<JurisdictionDetail />}
              />
              <Route path={ROUTES.WAGE_RULES} element={<Rules />} />
              <Route path={ROUTES.WAGE_RULE_DETAIL} element={<RuleDetail />} />
              <Route path="*" element={<NotFound />} />
              <Route path="/upcoming-listings" element={<UpcomingListings />} />
              <Route path={ROUTES.WORKER_SUPPLY} element={<WorkerSupply />} />
              <Route
                path={ROUTES.VERIFY_CREDENTIALS}
                element={<VerifyCredentials />}
              />
            </Route>
          </Routes>
        </main>
        <CommandPanel />
        <CommandPalette />
      </div>
    </CommandProvider>
  )
}

const router = createBrowserRouter(
  createRoutesFromElements(<Route path="*" Component={PageRoutes} />)
)

export function Pages() {
  return (
    <RouterProvider router={router} future={{ v7_startTransition: true }} />
  )
}
